import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import {
  Package,
  Clock,
  CheckCircle,
  AlertTriangle,
  Truck,
  User,
  ArrowRight,
} from "lucide-react";
import { getStatusBgColor, getStatusTextColor } from "@shared/utils/statusColors";
import { useNavigate } from "react-router-dom";
import { useAuth } from '@features/auth/contexts/AuthContext';
import * as pickupService from '@features/pickups/services/pickupService';

const PickupOverviewWidget = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const isLivreur = user?.role === "livreur";

  // Fetch recent pickups
  const { data: recentPickups, isLoading } = useQuery({
    queryKey: ["recent-pickups", user?.id],
    queryFn: async () => {
      if (isLivreur) {
        return pickupService.getLivreurPickups("limit=5");
      } else {
        return pickupService.getAllPickups({ limit: 5 });
      }
    },
    select: (data) => data?.pickups || [],
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <Clock className="h-4 w-4" />;
      case "approved":
        return <CheckCircle className="h-4 w-4" />;
      case "rejected":
        return <AlertTriangle className="h-4 w-4" />;
      case "picked_up":
        return <Package className="h-4 w-4" />;
      case "in_transit":
        return <Truck className="h-4 w-4" />;
      case "signed":
        return <CheckCircle className="h-4 w-4" />;
      case "failed":
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <Package className="h-4 w-4" />;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {t("Recent Pickups")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {isLivreur ? t("My Recent Pickups") : t("Recent Pickups")}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/pickups/management")}
            className="text-muted-foreground hover:text-foreground"
          >
            {t("View All")}
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {!recentPickups || recentPickups.length === 0 ? (
            <div className="text-center py-8">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {t("No pickups found")}
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                {isLivreur
                  ? t("You don't have any assigned pickups yet")
                  : t("No pickups have been created yet")}
              </p>
              {(isAdmin || isManager) && (
                <Button size="sm" onClick={() => navigate("/pickups/create")}>
                  {t("Create First Pickup")}
                </Button>
              )}
            </div>
          ) : (
            <>
              {recentPickups.map((pickup) => (
                <div
                  key={pickup._id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/pickups/${pickup._id}`)}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted">
                      {getStatusIcon(pickup.status)}
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">
                          {pickup.type === "package"
                            ? t("Package Pickup")
                            : t("Product Pickup")}
                        </span>
                        <Badge
                          variant="outline"
                          className={`${getStatusBgColor(pickup.status)} ${getStatusTextColor(pickup.status)}`}
                        >
                          {pickup.status?.replace(/_/g, " ").toUpperCase()}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>{pickup.pickupCity}</span>
                        {pickup.livreur && (
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            <span>{pickup.livreur.fullName}</span>
                          </div>
                        )}
                        {pickup.type === "package" && (
                          <span>
                            {pickup.packages?.length || 0} {t("packages")}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(pickup.updatedAt).toLocaleDateString()}
                  </div>
                </div>
              ))}

              {/* Quick Action Button */}
              <div className="pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => navigate("/pickups/management")}
                >
                  <Truck className="h-4 w-4 mr-2" />
                  {isLivreur ? t("Manage My Pickups") : t("Manage All Pickups")}
                </Button>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PickupOverviewWidget;
